import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { windowLabel } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Star, Copy, Share2, Plus, X as XIcon, Calendar as CalendarIcon, Award, Flame, Sparkles, Trophy, Map as MapIcon, Bell, MessageSquare, Users, Shield,
} from "lucide-react";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import Countdown from "@/components/Countdown";
import MysteryRewardPopup from "@/components/MysteryRewardPopup";
import ContestBanner from "@/components/ContestBanner";
import BirthdayBanner from "@/components/BirthdayBanner";

const FREE_WASH = 5000;

export default function CustomerDashboard() {
  const { user, refreshMe } = useAuth();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [pointsLog, setPointsLog] = useState([]);
  const [badges, setBadges] = useState([]);
  const [upsell, setUpsell] = useState(null);
  const [skipProt, setSkipProt] = useState({ free_remaining: 0, paid_price: 2 });
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, s, p, b, up, sp] = await Promise.all([
        api.get("/orders/my"),
        api.get("/subscriptions/my"),
        api.get("/points-log/my").catch(() => ({ data: [] })),
        api.get("/badges").catch(() => ({ data: [] })),
        api.get("/upsell/my").catch(() => ({ data: { suggestion: null } })),
        api.get("/skip-protection/my").catch(() => ({ data: { free_remaining: 0, paid_price: 2 } })),
      ]);
      setOrders(o.data);
      setSubs(s.data);
      setPointsLog(p.data);
      setBadges(b.data);
      setUpsell(up.data);
      setSkipProt(sp.data);
      await refreshMe().catch(() => {});
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  if (!user) return <div className="p-12 text-center text-slate-500">Please log in.</div>;
  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;

  const upcoming = orders.filter((o) => ["scheduled"].includes(o.status) && o.payment_status !== "canceled")
    .sort((a, b) => (a.pickup_date || "").localeCompare(b.pickup_date || ""));
  const past = orders.filter((o) => ["completed", "picked_up", "skipped", "canceled"].includes(o.status));
  const next = upcoming[0];

  const points = user.dash_points || 0;
  const userBadges = user.badges || [];
  const pct = Math.min(100, (points / FREE_WASH) * 100);
  const remaining = Math.max(0, FREE_WASH - points);

  const copyCode = () => {
    navigator.clipboard.writeText(user.referral_code);
    toast.success("Referral code copied!");
  };

  const share = async () => {
    const url = `${window.location.origin}/register?ref=${user.referral_code}`;
    const text = `Try Bag Dash Kids! Use my code ${user.referral_code} when you sign up — when 3 friends complete a paid pickup with my code, I get a free bag pickup. ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Bag Dash Kids", text, url });
        return;
      } catch { /* fallthrough */ }
    }
    navigator.clipboard.writeText(text);
    toast.success("Share text copied — paste it anywhere!");
  };

  const skipNext = async () => {
    if (!next) return;
    const protectAvailable = skipProt.free_remaining > 0;
    const useProtection = window.confirm(
      `Skip your ${windowLabel(next.window)} pickup on ${next.pickup_date}?\n\n` +
      (protectAvailable
        ? `🛡️ Use your FREE Skip Protection (${skipProt.free_remaining} left this month) to keep your streak?\n\nOK = use protection · Cancel = skip without protection (resets streak)`
        : "This will reset your streak. (Skip Protection: $2 Skip Pass available — coming soon. For now, Cancel and try again later.)")
    );
    try {
      if (useProtection && protectAvailable) {
        await api.post(`/orders/${next.id}/skip-protected`, { paid: false });
        toast.success("🛡️ Skip protected. Streak saved!");
      } else {
        await api.post(`/orders/${next.id}/skip`);
        toast.success("Pickup skipped");
      }
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not skip"); }
  };

  const addBag = async () => {
    if (!next) return;
    try {
      await api.put(`/orders/${next.id}/add-bag`);
      toast.success("Added a bag!");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add bag"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900" data-testid="dashboard-title">Hi {user.name.split(" ")[0]} 👋</h1>
          <p className="text-slate-600 mt-1 text-sm">Welcome back. Here's your dash.</p>
        </div>
      </div>

      <div className="mb-4"><AnnouncementBanner /></div>
      <div className="mb-4"><ContestBanner /></div>
      <MysteryRewardPopup />

      {/* Streak flame */}
      {user.streak > 0 && (
        <div className="card-clay p-4 mb-4 bg-gradient-to-r from-orange-50 to-red-50 border-orange-200 flex items-center gap-3" data-testid="streak-flame">
          <div className="text-3xl">🔥</div>
          <div className="flex-1">
            <div className="font-fredoka font-bold text-orange-900">{user.streak}-pickup streak!</div>
            <div className="text-xs text-orange-800">Keep going — 4-pickup streaks earn <b>2x points</b>. {skipProt.free_remaining > 0 ? `${skipProt.free_remaining} Skip Protection left this month.` : ""}</div>
          </div>
          {skipProt.free_remaining > 0 && <Shield className="text-orange-600 hidden sm:block" strokeWidth={2.5}/>}
        </div>
      )}

      {/* Smart upsell */}
      {upsell?.suggestion && (
        <Link to={upsell.cta_link} className="block card-clay p-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" data-testid="upsell-card">
          <div className="flex items-center gap-3">
            <div className="text-3xl">💡</div>
            <div className="flex-1">
              <div className="font-fredoka font-bold text-blue-900">{upsell.title}</div>
              <div className="text-xs text-blue-800">{upsell.body}</div>
            </div>
            <span className="btn-push-navy text-xs px-3 py-1.5 whitespace-nowrap">{upsell.cta} →</span>
          </div>
        </Link>
      )}

      {/* Next pickup hero */}
      {next ? (
        <div className="card-clay p-6 bg-gradient-to-br from-green-50 to-emerald-100/40 border-green-200 mb-4" data-testid="next-pickup-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-green-800">Your next pickup</div>
              <div className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mt-1">
                {windowLabel(next.window)}
              </div>
              <div className="text-sm text-slate-700 mt-1">
                {next.pickup_date} · {next.bags} bag(s){next.monthly_cleaning ? " + bin cleaning" : ""}
              </div>
              <div className="text-base font-bold text-green-700 mt-2">
                Pickup <Countdown targetDate={next.pickup_date} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={skipNext} className="btn-push-outline px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="skip-pickup">
                <XIcon size={14}/> Skip this week
              </button>
              <button onClick={addBag} className="btn-push-amber px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="add-bag">
                <Plus size={14}/> Add a bag
              </button>
              <button onClick={share} className="btn-push-green px-4 py-2 text-sm inline-flex items-center gap-1" data-testid="share-quick">
                <Share2 size={14}/> Share my code
              </button>
            </div>
          </div>
          {next.payment_status !== "paid" && (
            <div className="mt-3 text-sm">
              <span className="bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider mr-2">{next.payment_status === "pay_in_person" ? "Pay in Person" : next.payment_status}</span>
              {next.payment_status === "pending" && (
                <Link to={`/checkout/${next.id}`} className="text-green-700 underline font-semibold">Pay now</Link>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="card-clay p-6 mb-4 text-center">
          <div className="font-fredoka font-bold text-xl">No upcoming pickups</div>
          <p className="text-slate-500 mt-1">Get ahead of the trash.</p>
          <Link to="/schedule" className="btn-push-green inline-block mt-3 px-5 py-2.5" data-testid="dash-schedule-cta">Schedule a Pickup</Link>
        </div>
      )}

      {/* Rewards progress */}
      <div className="card-clay p-6 mb-4 bg-gradient-to-br from-amber-50 to-amber-100/60 border-amber-200" data-testid="rewards-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-10 h-10 rounded-2xl bg-amber-500 border-b-4 border-amber-700 flex items-center justify-center">
            <Trophy className="text-white" strokeWidth={2.5} size={20}/>
          </div>
          <div className="flex-1">
            <div className="font-fredoka font-bold text-amber-900">Dash Rewards</div>
            <div className="text-xs text-amber-800">5,000 points = FREE bin cleaning trophy 🏆</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-800">Points</div>
            <div className="font-fredoka text-3xl font-bold text-slate-900" data-testid="dash-points">{points.toLocaleString()}</div>
          </div>
        </div>
        <div className="mt-3 h-4 bg-white rounded-full border-2 border-amber-200 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-amber-900 font-semibold">
          <span>{remaining > 0 ? `${remaining.toLocaleString()} pts to the trophy` : "Trophy unlocked! See your admin."}</span>
          <Link to="/leaderboard" className="underline" data-testid="rewards-leaderboard-link">View leaderboard →</Link>
        </div>
      </div>

      {/* Referral widget */}
      <div className="card-clay p-6 mb-4" data-testid="referral-widget">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Your referral code</div>
            <div className="font-fredoka text-4xl font-bold text-slate-900 mt-1 select-all" data-testid="referral-code">{user.referral_code}</div>
            <div className="text-xs text-slate-600 mt-1.5 leading-relaxed">
              <b>Refer 3 friends or neighbors</b> who complete a paid pickup → you earn <b>1 FREE bag pickup</b>. Referrals only count after they pay for their first pickup.
            </div>
            {(user.free_pickup_credits || 0) > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-green-100 text-green-800 border border-green-200" data-testid="free-pickup-credits-badge">
                🎁 You have {user.free_pickup_credits} FREE pickup{user.free_pickup_credits > 1 ? "s" : ""} ready to use
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={copyCode} className="btn-push-outline px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="copy-referral"><Copy size={14}/> Copy</button>
            <button onClick={share} className="btn-push-amber px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="share-referral"><Share2 size={14}/> Share</button>
          </div>
        </div>
      </div>

      {/* Badges */}
      {userBadges.length > 0 && (
        <div className="card-clay p-5 mb-4" data-testid="badges-section">
          <div className="flex items-center gap-2 mb-3">
            <Award size={18} className="text-slate-700"/>
            <div className="font-fredoka font-bold text-lg">Your badges</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {userBadges.map((k) => {
              const meta = badges.find((b) => b.key === k);
              return (
                <span key={k} className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 border-2 border-amber-200 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                  {iconFor(meta?.icon)} {meta?.label || k}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid sm:grid-cols-5 gap-3 mb-4">
        <QuickLink to="/calendar" icon={<CalendarIcon size={20}/>} label="Calendar" testid="ql-calendar"/>
        <QuickLink to="/plan" icon={<Sparkles size={20}/>} label="My Plan" testid="ql-plan"/>
        <QuickLink to="/billing" icon={<Trophy size={20}/>} label="Billing" testid="ql-billing"/>
        <QuickLink to="/community" icon={<Users size={20}/>} label="Community" testid="ql-community"/>
        <QuickLink to="/map" icon={<MapIcon size={20}/>} label="Service Areas" testid="ql-map"/>
      </div>

      {/* Activity feed */}
      <div className="card-clay p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={18}/>
          <div className="font-fredoka font-bold text-lg">Recent activity</div>
        </div>
        {pointsLog.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No activity yet — schedule a pickup to start earning.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="activity-feed">
            {pointsLog.slice(0, 8).map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between text-sm" data-testid={`activity-${p.id}`}>
                <div>
                  <div className="font-semibold text-slate-800 capitalize">{p.reason.replace(/_/g, " ")}</div>
                  <div className="text-xs text-slate-400">{new Date(p.created_at).toLocaleString()}</div>
                </div>
                <div className="font-fredoka font-bold text-amber-700">+{p.amount}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Upcoming list (compact) */}
      {upcoming.length > 1 && (
        <Section title="Upcoming">
          <div className="grid sm:grid-cols-2 gap-3">
            {upcoming.slice(1).map((o) => <MiniOrder key={o.id} o={o}/>)}
          </div>
        </Section>
      )}

      {past.length > 0 && (
        <Section title="Past pickups">
          <div className="grid sm:grid-cols-2 gap-3">
            {past.slice(0, 6).map((o) => <MiniOrder key={o.id} o={o} muted/>)}
          </div>
          {past.some((o) => !o.review_id && o.status === "picked_up") && (
            <div className="mt-3 text-sm text-slate-500">
              <Link to="/reviews/new" className="text-green-700 font-bold underline" data-testid="leave-review-link">Leave a review</Link> for one of your completed pickups (+50 pts).
            </div>
          )}
        </Section>
      )}

      {/* Subscriptions */}
      {subs.length > 0 && (
        <Section title="Weekly recurring">
          <div className="grid sm:grid-cols-2 gap-3">
            {subs.map((s) => (
              <div key={s.id} className="card-clay p-5" data-testid={`sub-${s.id}`}>
                <div className="font-fredoka font-bold">{s.community}</div>
                <div className="text-xs text-slate-500">{s.windows.map(windowLabel).join(", ")}</div>
                <Link to="/plan" className="btn-push-outline inline-block mt-3 px-3 py-1.5 text-xs">Manage plan</Link>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="card-clay p-5 bg-slate-50 mt-6 flex items-center gap-3 flex-wrap" data-testid="feedback-card">
        <MessageSquare className="text-slate-700" strokeWidth={2.5}/>
        <div className="text-sm text-slate-700 font-semibold flex-1">Got feedback or a question?</div>
        <Link to="/contact" className="btn-push-outline px-4 py-2 text-sm">Contact us</Link>
      </div>
    </div>
  );
}

const QuickLink = ({ to, icon, label, testid }) => (
  <Link to={to} className="card-clay p-4 flex items-center gap-3 hover:scale-[1.02] transition-all" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-green-100 text-green-700 flex items-center justify-center">{icon}</div>
    <div className="font-fredoka font-bold text-slate-900">{label}</div>
  </Link>
);

const Section = ({ title, children }) => (
  <section className="mb-6">
    <h2 className="font-fredoka text-xl font-bold text-slate-900 mb-3">{title}</h2>
    {children}
  </section>
);

const MiniOrder = ({ o, muted }) => (
  <div className={`card-clay p-4 ${muted ? "opacity-80" : ""}`} data-testid={`order-${o.id}`}>
    <div className="flex items-center justify-between">
      <div className="font-fredoka font-bold">{windowLabel(o.window)}</div>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${pillClass(o.status)}`}>{o.status}</span>
    </div>
    <div className="text-xs text-slate-500">{o.pickup_date || new Date(o.created_at).toLocaleDateString()}</div>
    <div className="text-sm text-slate-700 mt-1">{o.bags} bag(s) · ${o.total_amount.toFixed(2)}</div>
  </div>
);

const pillClass = (status) => ({
  scheduled: "bg-blue-100 text-blue-800",
  picked_up: "bg-green-100 text-green-800",
  completed: "bg-green-100 text-green-800",
  skipped: "bg-amber-100 text-amber-800",
  canceled: "bg-slate-200 text-slate-700",
}[status] || "bg-slate-100 text-slate-700");

const iconFor = (name) => {
  switch (name) {
    case "trophy": return <Trophy size={14}/>;
    case "flame": return <Flame size={14}/>;
    case "star": return <Star size={14}/>;
    default: return <Award size={14}/>;
  }
};
